"use client";

import { c as _c } from "react/compiler-runtime";
import remarkGfm from "remark-gfm";
import { type ReactNode, useEffect, useState } from "react";
import { HiXMark } from "react-icons/hi2";
import ReactMarkdown from "react-markdown";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { cx } from "@/lib/tailwind-utils";
type BannerSeverity = "warning" | "destructive";
type BannerConfig = {
  enabled: boolean;
  message: string;
  severity: BannerSeverity;
};
const DISMISSED_STORAGE_KEY = "service-disruption-banner-dismissed-message";
function isTruthy(value: string): boolean {
  return value.toLowerCase() === "true" || value === "1";
}
function parseSeverity(value: string): BannerSeverity {
  return value.toLowerCase() === "destructive" ? "destructive" : "warning";
}
function readBannerConfig(): BannerConfig {
  const env = window.RUNTIME_ENV ?? {};
  return {
    enabled: isTruthy(env.NEXT_PUBLIC_SERVICE_BANNER_ENABLED ?? ""),
    message: (env.NEXT_PUBLIC_SERVICE_BANNER_MESSAGE ?? "").trim(),
    severity: parseSeverity(env.NEXT_PUBLIC_SERVICE_BANNER_SEVERITY ?? "")
  };
}
export default function ServiceDisruptionBanner() {
  const $ = _c(18);
  const [config, setConfig] = useState(null);
  const [dismissedMessage, setDismissedMessage] = useState(null);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      setConfig(readBannerConfig());
      try {
        setDismissedMessage(localStorage.getItem(DISMISSED_STORAGE_KEY));
      } catch {
        setDismissedMessage(null);
      }
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  if (!config || !config.enabled || !config.message) {
    return null;
  }
  if (dismissedMessage === config.message) {
    return null;
  }
  let t2;
  if ($[2] !== config.message) {
    t2 = () => {
      try {
        localStorage.setItem(DISMISSED_STORAGE_KEY, config.message);
      } catch {}
      setDismissedMessage(config.message);
    };
    $[2] = config.message;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleDismiss = t2;
  const dismissColorClass = config.severity === "destructive" ? "text-red-900 hover:bg-red-100 dark:text-red-200 dark:hover:bg-red-900/50" : "text-yellow-900 hover:bg-yellow-100 dark:text-yellow-200 dark:hover:bg-yellow-900/50";
  const t3 = config.severity;
  let t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [remarkGfm];
    t5 = {
      p: _temp,
      a: _temp2
    };
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  let t6;
  if ($[6] !== config.message) {
    t6 = <AlertDescription className="px-8 text-body-lg"><ReactMarkdown remarkPlugins={t4} components={t5}>{config.message}</ReactMarkdown></AlertDescription>;
    $[6] = config.message;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== dismissColorClass) {
    t7 = cx("absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 transition-colors", dismissColorClass);
    $[8] = dismissColorClass;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <HiXMark className="h-5 w-5" aria-hidden="true" />;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== handleDismiss || $[12] !== t7) {
    t9 = <button type="button" onClick={handleDismiss} aria-label="Dismiss service disruption banner" className={t7}>{t8}</button>;
    $[11] = handleDismiss;
    $[12] = t7;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== config.severity || $[15] !== t6 || $[16] !== t9) {
    t10 = <Alert variant={t3} className="rounded-none border-x-0 border-t-0 text-center [&>svg]:hidden [&>svg~*]:pl-0">{t6}{t9}</Alert>;
    $[14] = config.severity;
    $[15] = t6;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  return t10;
}
function _temp2(t0) {
  const {
    href,
    children: children_0
  } = t0;
  if (!href) {
    return <span>{children_0}</span>;
  }
  const isExternal = href.startsWith("http");
  return <a href={href} target={isExternal ? "_blank" : undefined} rel={isExternal ? "noopener noreferrer" : undefined} className="font-medium underline underline-offset-2 hover:opacity-80">{children_0}</a>;
}
function _temp(t0) {
  const {
    children
  } = t0;
  return <>{children}</>;
}